export type Challenge = {
    is_batter: boolean
    is_in_progress?: boolean
    is_overturned: boolean
    challenge_team_id: number
    edge_distance?: number
    edge_distance_calc?: number
    is_on_fly?: boolean
}

export type GamefeedRawData = {
    source: 'gamefeed'
    type: string
    play_id: string
    inning: number
    ab_number: number
    cap_index: number
    outs: number
    batter: number
    stand: string
    batter_name: string
    pitcher: number
    p_throws: string
    pitcher_name: string
    catcher: number
    catcher_name: string
    team_batting: string
    team_fielding: string
    team_batting_id: number
    team_fielding_id: number
    result: string
    des: string
    events: string
    contextMetrics: {
        homeRunBallparks?: number
    }
    strikes: number
    balls: number
    pre_strikes: number
    pre_balls: number
    call: string
    call_name: string
    pitch_type: string
    pitch_name: string
    description: string
    result_code: string
    pitch_call: string
    is_strike_swinging: boolean
    balls_and_strikes: string
    start_speed: string
    end_speed: number
    sz_top: number
    sz_bot: number
    sz_depth: number
    sz_width: number
    extension: number
    plateTime: number
    zone: number
    spin_rate: number
    px: number
    pz: number
    x0: number
    y0: number
    z0: number
    ax: number
    ay: number
    az: number
    vx0: number
    vy0: number
    vz0: number
    pfxX: number
    pfxZ: number
    pfxZWithGravity: number
    pfxZWithGravityNice: number
    pfxZDirection: string
    pfxXWithGravity: number
    pfxXNoAbs: string
    pfxXDirection: string
    plateTimeSZDepth: number
    plateXPoly: number
    plateYPoly: number
    plateZPoly: number
    breakX: number
    breakZ: number
    inducedBreakZ: number
    inducedBreakZForcedSign: string
    ivbZDirection: string
    isSword: boolean
    is_bip_out: string
    pitch_number: number
    player_total_pitches: number
    player_total_pitches_pitch_types: number
    pitcher_pa_number: number
    pitcher_time_thru_order: number
    game_total_pitches: number
    rowId: string
    game_pk: string
    plate_x: number
    plate_z: number
    hit_speed?: string
    hit_angle?: string
    batSpeed?: number
    hc_x?: number
    hc_y?: number
    hc_x_ft?: number
    hc_y_ft?: number
    is_abs_challenge: boolean
    abs_challenge?: Required<Challenge>
    runnerOn1B?: boolean
    runnerOn2B?: boolean
    runnerOn3B?: boolean
    // seems wrong...
    // pfx_x: number
    // pfx_z_gravity: number
}

// When parsing from a CSV everything is a string, so that's tricky...
// We should be very careful not to parse Boolean('0') and expect false.
export type IllustratorRawData = {
    source?: 'illustrator'
    player_id: string
    batter_name: string
    pitcher_name: string
    batter: string
    pitcher: string
    venue: string
    team_batting: string
    team_pitching: string
    pitch_type: string
    game_date: string
    release_speed: string
    event_type: string
    is_lastpitch: string | number
    release_pos_x: string
    release_pos_y: string
    release_pos_z: string
    ba_numer: string
    ba_denom: string
    est_ba_numer: string
    est_ba_denom: string
    bacon_numer: string
    bacon_denom: string
    est_bacon_numer: string
    est_bacon_denom: string
    babip_numer: string
    babip_denom: string
    obp_numer: string
    obp_denom: string
    slg_numer: string
    slg_denom: string
    est_obp_numer: string
    est_obp_denom: string
    est_slg_numer: string
    est_slg_denom: string
    iso_numer: string
    iso_denom: string
    est_iso_numer: string
    est_iso_denom: string
    fangraphs_woba_numer: string
    fangraphs_woba_denom: string
    fangraphs_est_woba_numer: string
    fangraphs_est_woba_denom: string
    fangraphs_wobacon_numer: string
    fangraphs_wobacon_denom: string
    fangraphs_est_wobacon_numer: string
    fangraphs_est_wobacon_denom: string
    zone: string
    api_gameday3_pitchzone_cd: string
    events: string
    game_type: string
    stand: string
    p_throws: string
    home_team: string
    away_team: string
    home_team_id: string
    away_team_id: string
    batting_team_id: string
    fielding_team_id: string
    hit_location: string
    bb_type: string
    balls: string
    strikes: string
    pre_ball_count: string
    pre_strike_count: string
    game_year: number
    year: string
    plate_x: string
    plate_z: string
    px: string
    pz: string
    py: string
    on_3b: string
    on_2b: string
    on_1b: string
    outs_when_up: string
    inning: string
    inning_topbot: string
    hc_x?: string
    hc_y?: string
    hc_x_ft?: string
    hc_y_ft?: string
    sv_id: string
    vx0: string
    vy0: string
    vz0: string
    ax: string
    ay: string
    az: string
    x0: string
    y0: string
    z0: string
    sz_top: string
    sz_bot: string
    sz_depth: number | string
    is_hit_into_play_basehit: string | number
    is_hit_into_play: string | number
    is_ab: string | number
    is_pa: string | number
    is_whiff: string | number
    is_swing: string | number
    is_take: string | number
    is_strikeout: string | number
    is_walk: string | number
    is_hit_into_play_basehit_single: string | number
    is_hit_into_play_basehit_double: string | number
    is_hit_into_play_basehit_triple: string | number
    is_hit_into_play_basehit_home_run: string | number
    is_sac: string | number
    is_sf: string | number
    is_foul: string | number
    play_id: string
    hit_distance_sc: string
    launch_angle?: string
    launch_speed?: string
    h_launch_angle?: string
    h_launch_speed?: string
    launch_direction: string
    effective_speed: string
    release_spin_rate: string | number
    release_extension: string
    game_pk: string
    distance_projected: string
    slg_value: string
    iso_value: string
    woba_value: string
    woba_denom: string
    estimated_slg_using_speedangle: string
    h_launch_speedangle_code: string
    babip_value: string
    pos1_int_start_distance: string
    pos2_int_start_distance: string
    pos3_int_start_distance: string
    pos4_int_start_distance: string
    pos5_int_start_distance: string
    pos6_int_start_distance: string
    pos7_int_start_distance: string
    pos8_int_start_distance: string
    pos9_int_start_distance: string
    launch_speed_angle: string
    home_name_abbrev: string
    venue_name: string
    venue_id: string
    touch1_fielder_pos: string
    pitch_name: string
    type: string
    at_bat_number: string
    pitch_number: string
    estimated_ba_using_speedangle: string
    is_pitch: string | number
    description: string
    delta_run_exp: string
    is_competitive: string | number
    sweetspot_speed_mph: string
    swing_length: string
    stringer_hit_trajectory: string
    ideal_angle: number
    attack_angle?: number
    attack_direction?: number
    swing_path_tilt?: number
    is_abs_challenge: string | number
    is_abs_overturned: string | number
    abs_challenge_team_id: string | number

    // These are duplicates of api_break_x etc TODO: Remove someday
    pfx_x: string
    pfx_z: string
    pfx_z_gravity: string
}

export function isGamefeedRawData(
    raw: GamefeedRawData | IllustratorRawData
): raw is GamefeedRawData {
    return raw.source === 'gamefeed'
}

// TODO: a d.ts file wasn't picked up for me, is there a better way to have some globally available types?
export type EVPData = {
    x: number | null
    y: number | null
    pitchType: string | null
    pitchName: string | null
    legend: string | null
    paResult: string | null
    pitchResult: string | null
    hits?: number
    battedBallType?: string | null
    ab?: number
    pa?: number
    bip?: number
    trackedBIP?: number
    trackedSwingLength?: number
    trackedBatSpeed: number
    trackedSwingLengthCompetitive?: number
    trackedBatSpeedCompetitive?: number
    single?: number
    double?: number
    triple?: number
    hr?: number
    so?: number
    zone: number | string | null
    callName?: string | null
    balls: number
    strikes: number
    releaseSpeed: number
    releaseSpinRate: number
    pfxX: number | null
    pfxZGravity: number | null
    launchSpeed?: number
    launchAngle?: number
    swingLength?: number | null
    batSpeed: number | null
    is_swing?: number | null
    is_whiff?: number | null
    deltaRunExp?: number
    wobaNumer?: number
    wobaDenom?: number
    xWobaNumer?: number
    xWobaDenom?: number
    tooltip: string | null
    videoUrl?: string | null
    // TODO: Or...?
    raw: GamefeedRawData | IllustratorRawData
    hasStrikeZone: boolean
    szBot: number | null
    szTop: number | null
    szDepth: number | null
    szWidth: number | null
    event_type?: string // why was this commented out? :)
    description?: string
    call_name?: string

    eventFormatted?: string

    is_abs_challenge: boolean
    abs_challenge?: Challenge
    isInZone: boolean
}

export const strikeZoneTypes = [
    'personal',
    // 'generic-old',
    'generic',
    'fuzzy',
    'average',
    // 'relative', // TODO: Consider relative
] as const

export type StrikeZone = (typeof strikeZoneTypes)[number]

export type ChartOptionsType =
    | 'totalPitches'
    | 'percent'
    | 'ba'
    | 'woba'
    | 'avgExitVelocity'
    | 'avgLaunchAngle'
    | 'batSpeedCompetitive'
    | 'swingLengthCompetitive'
    | 'kPercent'
    | 'runExp'
    // This is used throughout the site, but unclear if it does anything.
    | 'pitchName'

export type ChartType =
    | '3d'
    | 'field'
    | 'line'
    | 'movement'
    | 'network'
    | 'pitch'
    | 'radial'
    | 'release'
    | 'sideways'

export type ShowHideFlags = {
    showZoneDepthText?: boolean
    showZoneScaleText?: boolean
}

export type ChartOptions = {
    index?: number
    skipDataProcess?: boolean
    showText?: boolean
    chartType: ChartType
    type: ChartOptionsType
    // TODO: THE ABOVE ISN'T COMPLETE
    showRHB: boolean
    showLHB: boolean
    showPlate: boolean
    overlayType: string
    showStrikeZone: boolean
    showFieldDegreeMarkers?: boolean
    pitcherPerspective?: boolean
    normalizedStrikeZone?: boolean
    legendType: keyof EVPData
    opacity?: string | number | undefined
    labelPitches?: boolean
    legend: boolean
    strikeZoneBorderOpacity?: number
    strikeZoneFillOpacity?: number
    pitcherView?: boolean
    strikeZone?: StrikeZone
    normalize?: boolean
    scaleRadius?: boolean
    // Used for multiple fields, only show the first one
    showField?: boolean
    // Didn't want to call it options, my idea is
    // these are where we could collect true/false properties
    // which hide/show things
    flags?: ShowHideFlags
}

export type Settings = {
    data: EVPData[]
    charts: ChartOptions[]
    vizHeight: number
    vizWidth: number
    vizScale: number
    orientation: 'portrait' | 'landscape'
    shape: string
    venueId?: number
    enableZoom?: boolean
}
